import React from 'react';

/**
 * Teamleistungslohn: Funktionserklärung (1–6) + Zusatzvereinbarung (1–7).
 * Wortlaut 1:1 laut Master-Briefing — nicht kürzen oder umformulieren.
 */
export const TeamLeistungslohnDocument: React.FC = () => (
    <>
        {/* --- SEITE 1: FUNKTIONSERKLÄRUNG --- */}
        <div className="text-center mb-8 border-b-2 border-black pb-4">
            <h1 className="text-3xl font-black tracking-widest uppercase mb-1 font-sans">PreisExpert</h1>
            <h2 className="text-xs font-bold tracking-widest uppercase text-gray-600 font-sans">Das Betriebssystem für erfolgreiche Friseure</h2>
        </div>

        <h3 className="text-xl font-bold mb-2 uppercase bg-gray-100 p-2 text-center border border-gray-300">PreisExpert – Teamleistungslohnsystem</h3>
        <p className="text-center text-xs font-bold uppercase tracking-wider text-gray-500 mb-8 border-b border-gray-300 pb-2">Bitte dem/der Mitarbeiter/in zur Information aushändigen</p>

        <div className="space-y-4 text-justify text-[13px] leading-relaxed">
            <h4 className="font-bold text-[15px] border-b border-gray-300 pb-1 mt-6">Funktionen:</h4>

            <p>
                <strong className="font-bold">1. Betriebswirtschaftliche Zielermittlung (Salon-Zielumsatz):</strong>{' '}
                Im PreisExpert-Kalkulationstool wird auf Basis einer transparenten Kostenkalkulation der monatliche Salon-Zielumsatz ermittelt. Dieser Betrag deckt alle laufenden Gesamtkosten des Salons sowie die betriebswirtschaftlich notwendigen Rücklagen ab.
            </p>

            <p>
                <strong className="font-bold">2. Umsatzerfassung &amp; Bonus-Schwelle:</strong>{' '}
                Im System werden monatlich die tatsächlich erbrachten Umsätze des Salons erfasst (Bedien- und Verkaufsumsatz). Ein Teamleistungslohn wird ausschließlich dann ausgelöst, wenn das Team in der Gesamtheit den kalkulierten Salon-Zielumsatz übertrifft.
            </p>

            <p>
                <strong className="font-bold">3. Team-Bonus-Topf:</strong>{' '}
                Wird der kalkulierte Salon-Zielumsatz übertroffen, fließt ein vom Arbeitgeber festgelegter Prozentsatz des erwirtschafteten Überschusses in einen gemeinsamen Team-Bonus-Topf.
            </p>

            <p>
                <strong className="font-bold">4. Keine fiktiven Ausgleiche bei Abwesenheiten:</strong>{' '}
                Im Teamleistungslohnsystem fließen ausschließlich real erwirtschaftete Salonumsätze ein. Für Urlaubs-, Fehl- oder Krankheitstage werden keine fiktiven Umsätze gutgeschrieben; maßgeblich ist das tatsächliche Gesamtergebnis des Salons.
            </p>

            <p>
                <strong className="font-bold">5. Verteilungsschlüssel (Produktives Grundgehalt):</strong>{' '}
                Der erwirtschaftete Team-Bonus-Topf wird unter den berechtigten Mitarbeiter*innen aufgeteilt. Die Verteilung erfolgt standardmäßig proportional zum vertraglich vereinbarten produktiven Brutto-Grundgehalt. Rein unproduktive Gehaltsbestandteile (z. B. für administrative Leitungsaufgaben) bleiben in der Verteilungsbasis unberücksichtigt. Optionale Einbindung: Der Arbeitgeber kann im System betriebsindividuell festlegen, ob und mit welcher Gewichtung unproduktive Mitarbeiter*innen (z. B. Empfang/Organisation) oder Auszubildende am Bonus-Topf partizipieren.
            </p>

            <p>
                <strong className="font-bold">6. Halbjahres-Auswertung &amp; Auszahlung:</strong>{' '}
                Zur Glättung saisonaler Schwankungen werden Monatsergebnisse über ein Halbjahr saldiert. Schließt ein Halbjahr im Minus ab, hat dies keinerlei negative Auswirkung auf das garantierte Grundgehalt. Ein positiver Gesamtsaldo wird nach Abrechnung ausgeschüttet.
            </p>
        </div>

        {/* --- SEITE 2: ZUSATZVEREINBARUNG --- */}
        <div className="print-break mt-16 pt-8">
            <div className="text-center mb-8 border-b-2 border-black pb-4 print:block hidden">
                <h1 className="text-3xl font-black tracking-widest uppercase mb-1 font-sans">PreisExpert</h1>
                <h2 className="text-xs font-bold tracking-widest uppercase text-gray-600 font-sans">Das Betriebssystem für erfolgreiche Friseure</h2>
            </div>

            <h3 className="text-xl font-bold mb-6 uppercase bg-gray-100 p-2 text-center border border-gray-300">Zusatzvereinbarung zum Arbeitsvertrag: Teamleistungslohn</h3>
            <p className="doc-kicker text-center text-xs font-bold uppercase tracking-wider text-gray-500 mb-6">(Dem Arbeitsvertrag beifügen)</p>

            <div className="doc-panel bg-gray-50 border-2 border-gray-800 p-8 space-y-6 text-[14px]">
                <p className="leading-loose">
                    <strong className="font-bold">1.</strong> Frau/Herr <span className="fill-line inline-block border-b border-black w-64 mx-2"></span> bezieht ein festes, zuvor vereinbartes Grundgehalt (aufgeteilt in produktive und ggf. unproduktive Vergütungsanteile). Der Teamleistungslohn ist eine variable Zusatzvergütung und lässt dieses feste Grundgehalt unberührt.
                </p>

                <p className="leading-loose">
                    <strong className="font-bold">2.</strong> Frau/Herr <span className="fill-line inline-block border-b border-black w-64 mx-2"></span> nimmt am kollektiven PreisExpert-Teamleistungslohnsystem teil. Die Auslösung eines Anspruchs setzt das Überschreiten des kalkulierten Salon-Zielumsatzes (Kosten + Rücklagen) durch das Gesamtergebnis des Salons voraus.
                </p>

                <p>
                    <strong className="font-bold">3.</strong> Der/die Arbeitnehmer*in bestätigt, die Funktionsbeschreibung verstanden zu haben und erklärt sich mit der Verteilung proportional zum produktiven Grundgehalt (bzw. dem im System definierten Teilnehmerkreis) einverstanden.
                </p>

                <p>
                    <strong className="font-bold">4. Transparenz &amp; Datenschutz (Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO):</strong> Der/die Arbeitnehmer*in willigt ausdrücklich ein, dass die im Salon erzielten Umsätze intern zur nachvollziehbaren Berechnung und gemeinsamen Visualisierung des Teamerfolgs verarbeitet und im Team offengelegt werden dürfen.
                </p>

                <p>
                    <strong className="font-bold">5.</strong> Die Auszahlung des Teamleistungslohns erfolgt als freiwillige Sonderleistung. Wiederholte Auszahlungen begründen keinen Rechtsanspruch für die Zukunft (Ausschluss betrieblicher Übung).
                </p>

                <div className="optional-box border border-dashed border-gray-400 p-4 mt-4 relative">
                    <span className="optional-badge absolute -top-3 left-4 bg-gray-50 px-2 text-[10px] uppercase font-bold text-gray-500">Ggf. streichen</span>
                    <p className="leading-loose">
                        <strong className="font-bold">6.</strong> Diese Zusatzvereinbarung wird auf das Geschäftsjahr 20<span className="fill-line-sm inline-block border-b border-black w-12 mx-1"></span> befristet geschlossen.
                    </p>
                </div>

                <p>
                    <strong className="font-bold">7. Salvatorische Klausel:</strong> Sollte eine Bestimmung dieser Vereinbarung unwirksam sein, wird die Wirksamkeit der übrigen Bestimmungen hiervon nicht berührt. Die Parteien verpflichten sich, die unwirksame Bestimmung durch eine Vereinbarung zu ersetzen, die der unwirksamen Bestimmung in Interessenlage und Bedeutung möglichst nahe kommt.
                </p>
            </div>

            <div className="print-signatures-grid mt-20 grid grid-cols-2 gap-16 px-4">
                <div className="signature-col">
                    <div className="sig-line border-b border-black mb-2 h-8"></div>
                    <p className="text-xs text-gray-600 font-bold uppercase tracking-wider">Ort, Datum</p>
                    <p className="text-[10px] text-gray-500 mt-1">(Unterschrift Arbeitgeber/in)</p>
                </div>
                <div className="signature-col">
                    <div className="sig-line border-b border-black mb-2 h-8"></div>
                    <p className="text-xs text-gray-600 font-bold uppercase tracking-wider">Ort, Datum</p>
                    <p className="text-[10px] text-gray-500 mt-1">(Unterschrift Arbeitnehmer/in)</p>
                </div>
            </div>
        </div>
    </>
);

export default TeamLeistungslohnDocument;
